<template>
    <div
        role="radiogroup"
        :aria-label="`Choose ${currentGroupName} Color`"
        class="cz2__color-selector-wrapper"
    >
        <div
            v-for="color in colors"
            :key="color.color"
        >
            <button
                @click.prevent="selectSwatchColor(color.color)"
                @keyup.space="selectSwatchColor(color.color)"
                :class="[
                    { selected: isSelected(color.color) },
                    { unavailable: color.isUnavailable },
                ]"
                :data-color="color.color"
                role="radio"
                :aria-checked="isSelected(color.color)"
                :aria-label="getAriaLabel(color)"
            >
                <div
                    :style="{ background: `linear-gradient(${colorValue(color.color, 0)}, ${colorValue(color.color, 1)})` }"
                />
            </button>
        </div>
    </div>
</template>

<style lang="scss">
    @use "sass:math";
    @import "../../style/variables.scss";

    #cz2.cz2 {
        .cz2__color-selector-wrapper {
            @include respond-above(md) {
                padding-top: 0;
            }
            button {
                height: 35px;
                width: 35px;
                border-radius: 50%;
                position: relative;
                overflow: visible;
                outline: none;

                &::before {
                    content: '';
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%) scale(1);
                    height: calc(100% + 8px);
                    width: calc(100% + 8px);
                    border-radius: 50%;
                    border: 3px solid transparent;
                    box-sizing: border-box;
                    transition: all 0.3s ease-out;
                    opacity: 0;
                }

                &:after {
                    content: '';

                    position: absolute;
                    top: 0;
                    left: 0;

                    // The intended size is 4px, shrink down by the transform size.
                    border: 4px solid transparent;
                    border-radius: 50%;

                    width: 100%;
                    height: 100%;

                    transition: border 0.2s ease-out;
                }

                &:focus {
                    &:after {
                        top: -2px;
                        left: -2px;

                        box-sizing: content-box;
                        border: 2px solid white;
                    }
                }

                &:not(.unavailable):active::before,
                &.selected::before {
                    border-color: color(secondaryYellow);
                    animation: ringExpandContract 0.7s ease-in-out forwards;
                    opacity: 1;
                }

                &.selected {
                    div {
                        animation: buttonSpring 0.7s ease-in-out forwards;
                        height: 80%;
                        width: 80%;
                        margin: 10%;
                    }

                    &:after {
                        border-color: transparent;
                    }
                }

                &:not(.selected) {
                    div {
                        border: 2px solid color(primaryWhite);
                    }
                }

                div {
                    border-radius: 50%;
                    height: 100%;
                    width: 100%;
                    transform-origin: center;
                }

                &.unavailable:after {
                    background-image: url('../../assets/icons/slash.svg');
                    background-repeat: no-repeat;
                    background-size: calc(100% + 16px);
                    background-position: center;
                    position: absolute;
                    top: 48%;
                    left: 48%;
                    transform: translate(-50%, -50%) scale(1);
                    height: 100%;
                    width: 100%;
                }
            }
        }
    }

    #cz2.cz2.cz2__is-kiosk {
        .cz2__color-selector-wrapper {
            padding-top: 24px;
            padding-bottom: 16px;
            button {
                height: 67.27px;
                width: 67.27px;
                &::before {
                    border: 6px solid transparent;
                }

                &.selected {
                    div {
                        height: 80%;
                        width: 80%;
                        margin: 9%;
                    }
                }

                &.selected::before {
                    border-color: color(secondaryYellow);
                    animation: ringExpandContract 0.7s ease-in-out forwards;
                    opacity: 1;
                }

                &:not(.selected) {
                    div {
                        border: 4px solid color(primaryWhite);
                    }
                }
            }
        }
    }

    @keyframes buttonSpring {
        0% { transform: scale(1); }
        15% { transform: scale(0.85); }
        30% { transform: scale(1.15); }
        45% { transform: scale(0.9); }
        55% { transform: scale(0.9); }
        70% { transform: scale(1.03); }
        100% { transform: scale(1); }
    }

    @keyframes ringExpandContract {
        0%, 100% { transform: translate(-50%, -50%) scale(1); }
        15% { transform: translate(-50%, -50%) scale(1.1); }
        30% { transform: translate(-50%, -50%) scale(0.9); }
        45% { transform: translate(-50%, -50%) scale(1.03); }
        55% { transform: translate(-50%, -50%) scale(1.03); }
        70% { transform: translate(-50%, -50%) scale(0.95); }
    }

    #cz2.cz2.cz2__is-mariners {
        .cz2__color-selector-wrapper {
            button {
                &:not(.unavailable):active::before,
                &.selected::before {
                    border-color: color(pokemonBlueLight);
                }
            }
        }
    }
</style>

<script>
    // TODO: right now this is for hair color only, might have to define multiple sets
    const colorNameMap = {
        Black: '#000000',
        Blonde: '#DFC772',
        Blue: '#2197DB',
        'Dark Blue': '#15567b',
        'Dark Dark Blue': '#0c3349',
        Brown: '#99735F',
        // This one is still needed... there is a typo in Funko data :facepalm:
        Burgandy: '#800020',
        Burgundy: '#800020',
        Clear: '#FFFFFF77',
        Cream: '#FFFDD0',
        Crimson: '#990000',
        Gold: '#FFD700',
        Gray: '#999999',
        Green: '#228b22',
        Pink: '#EAA9C3',
        Purple: '#7057AC',
        Red: '#f2752f', // was F16B6A
        Silver: '#BBBBBB',
        Tan: '#D2B48C',
        Turquoise: '#40E0D0',
        White: '#C7C7C7',
        Yellow: '#FFFF00',

        'Salt & Pepper': '#AAAAAA',
        'Dark Brown': '#6A4C3C',
        'Black/White': '#AAAAAA',
        'Blue/Green': '#AAAAAA',
        'Red/Blue': '#AAAAAA',
        'White/Red': '#AAAAAA',

        'N/A': 'transparent',
    };

    const colorSubstitutions = {
        Camo: 'Green/Brown',
    };

    export default {
        props: {
            colors: {
                type: Array,
                default: () => [],
            },
        },

        computed: {
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            currentGroupName() {
                return this.$store.state.customizer.currentGroup.name;
            },

            selectedSwatchColor() {
                return this.$store.state.customizer.selectedSwatchColors[this.currentGroupName];
            },
        },

        methods: {
            substituteColor(color) {
                return colorSubstitutions[color] ? colorSubstitutions[color] : color;
            },

            colorValue(colorIn, index) {
                // Allow to override colors.
                let colorMap = colorNameMap;
                if (this.viewController?.blueprint?.custom?.['fun-colors']) {
                    colorMap = JSON.parse(this.viewController?.blueprint?.custom?.['fun-colors']);
                }

                const color = this.substituteColor(colorIn);

                if (color === 'N/A') {
                    return 'transparent';
                }

                const baseColor = color.indexOf('/') !== -1 ? color.split('/')[index] : color;

                if (baseColor) {
                    if (colorMap[baseColor]) {
                        return colorMap[baseColor];
                    }
                    return `#${color}`;
                }

                return 'transparent';
            },

            selectSwatchColor(color) {
                this.$store.dispatch('setSelectedSwatchColors', color);
            },

            isSelected(color) {
                return this.selectedSwatchColor === color;
            },

            getAriaLabel(color) {
                return color.isUnavailable ? `${color.color} ${this.$t('UNAVAILABLE')}` : color.color;
            },
        },
    };
</script>
